'use client';

import {
	type KeyboardEvent,
	type RefObject,
	useId,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { type ChatCommand, filterChatCommands } from './commands';

export type CommandMenuMode = 'button' | 'slash';

type UseCommandMenuOptions = {
	commands: readonly ChatCommand[];
	inputRef?: RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
	value?: string;
	onChange?: (value: string) => void;
	onExecute?: (command: ChatCommand, source: CommandMenuMode) => void;
	onOpenChange?: (open: boolean, source: CommandMenuMode) => void;
};

function isSlash(text: string) {
	return text.trimStart().startsWith('/');
}

// Аналитика не должна ломать меню.
function track(callback: () => void) {
	try {
		callback();
	} catch {
		/* Analytics is optional. */
	}
}

export function useCommandMenu({
	commands,
	inputRef,
	value = '',
	onChange,
	onExecute,
	onOpenChange,
}: UseCommandMenuOptions) {
	const [open, setOpen] = useState(false);
	const [mode, setMode] = useState<CommandMenuMode>('button');
	const [active, setActive] = useState(0);
	const listId = useId();
	const trigger = useRef<HTMLButtonElement | null>(null);
	const openRef = useRef(false);
	const running = useRef(false);
	const focusHandled = useRef(false);
	const latest = useRef({ commands, value, onChange, onExecute, onOpenChange });
	useLayoutEffect(() => {
		latest.current = { commands, value, onChange, onExecute, onOpenChange };
	});
	const slashItems = useMemo(
		() => (isSlash(value) ? filterChatCommands(commands, value) : []),
		[commands, value],
	);
	const isCommand = slashItems.length > 0;
	const items: readonly ChatCommand[] =
		mode === 'slash' ? slashItems : commands;
	const selected = Math.min(active, Math.max(0, items.length - 1));

	// Ref отсекает повторное закрытие: Radix и keyDown оба реагируют на Escape.
	function changeOpen(
		next: boolean,
		source: CommandMenuMode,
		tracked: boolean,
	) {
		if (openRef.current === next) return;
		openRef.current = next;
		setOpen(next);
		if (tracked) track(() => latest.current.onOpenChange?.(next, source));
	}

	async function select(item?: ChatCommand) {
		if (!item || running.current) return;
		// Команда могла стать недоступной, пока меню было открыто.
		const command = latest.current.commands.find(({ id }) => id === item.id);
		if (!command || command.disabled) return;
		const source = mode;
		const before = latest.current.value;
		running.current = true;
		focusHandled.current = openRef.current;
		changeOpen(false, source, false);
		// Фокус возвращаем до запуска: после await его может забрать диалог или панель команды.
		if (source === 'slash') inputRef?.current?.focus({ preventScroll: true });
		else trigger.current?.focus({ preventScroll: true });
		track(() => latest.current.onExecute?.(command, source));
		try {
			const result = await command.run();
			if (
				source === 'slash' &&
				result !== false &&
				isSlash(before) &&
				latest.current.value === before
			)
				latest.current.onChange?.('');
		} catch {
			/* The action owns its error notice; keep the draft. */
		} finally {
			running.current = false;
		}
	}

	function changeText(text: string) {
		onChange?.(text);
		setActive(0);
		if (isSlash(text)) {
			setMode('slash');
			changeOpen(true, 'slash', true);
		} else if (mode === 'slash') changeOpen(false, 'slash', true);
	}

	function keyDown(
		event: KeyboardEvent<HTMLTextAreaElement | HTMLDivElement>,
	): boolean {
		if (event.nativeEvent.isComposing || event.keyCode === 229) return false;
		if (open) {
			switch (event.key) {
				case 'Escape':
					event.preventDefault();
					changeOpen(false, mode, true);
					return true;
				case 'Tab':
					changeOpen(false, mode, true);
					return false;
				case 'ArrowDown':
				case 'ArrowUp':
					if (!items.length) return false;
					event.preventDefault();
					setActive(
						(selected + (event.key === 'ArrowDown' ? 1 : -1) + items.length) %
							items.length,
					);
					return true;
				case 'Enter': {
					if (event.shiftKey) return false;
					const item = items[selected];
					// Неизвестная /команда уходит в onSend как обычный текст.
					if (!item) {
						changeOpen(false, mode, false);
						return false;
					}
					event.preventDefault();
					void select(item);
					return true;
				}
			}
			return false;
		}
		if (isCommand && event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			setMode('slash');
			setActive(0);
			changeOpen(true, 'slash', true);
			return true;
		}
		return false;
	}

	return {
		open,
		mode,
		items,
		selected,
		listId,
		isCommand,
		setTrigger(node: HTMLButtonElement | null) {
			trigger.current = node;
		},
		setActive(index: number) {
			setActive(index);
		},
		openMenu(next: boolean) {
			if (!next) {
				changeOpen(false, mode, true);
				return;
			}
			setMode('button');
			setActive(0);
			changeOpen(true, 'button', true);
		},
		select,
		changeText,
		keyDown,
		onCloseAutoFocus(event: Event) {
			if (mode === 'slash' || focusHandled.current) event.preventDefault();
			focusHandled.current = false;
		},
	};
}
